import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Avatar,
  Stack,
  Box,
  Grow,
  IconButton,
  Snackbar,
  Alert,
  useTheme,
} from '@mui/material';
import {
  LocationOn,
  LocalHospital,
  Star,
  Bookmark,
  BookmarkBorder,
} from '@mui/icons-material';
import BookMyOpAPI from '../services/BookMyOpapi';
import { useBookMyOpAuth } from '../contexts/BookMyOpAuthContext';
import { useFavorites } from '../contexts/FavoritesContext';
import { config } from '../config';

const HospitalCard = ({ hospital, index, onViewDoctors, showAnimation = true, selectedSpecialty }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { isAuthenticated } = useBookMyOpAuth();
  const { isHospitalFavorite } = useFavorites();
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Get favorite status from context
  const isFavorite = isHospitalFavorite(hospital.id);

  const handleToggleFavorite = async (e) => {
    e.stopPropagation();
    
    if (!isAuthenticated) {
      setSnackbar({
        open: true,
        message: 'Please login to save favorites',
        severity: 'warning'
      });
      return;
    }

    setLoading(true);
    try {
      const response = await BookMyOpAPI.toggleFavorite({ service_provider_id: hospital.id });
      setSnackbar({
        open: true,
        message: response.is_favorite ? 'Added to favorites!' : 'Removed from favorites',
        severity: 'success'
      });
      
      // Dispatch custom event to notify context to refresh
      window.dispatchEvent(new CustomEvent('favoritesUpdated', { 
        detail: { hospitalId: hospital.id, isFavorite: response.is_favorite } 
      }));
    } catch (error) {
      console.error('Error toggling favorite:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update favorites',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };
  
  const handleClick = () => {
    navigate(`/hospitals/${hospital.id}`);
  };

  const handleViewDoctorsClick = (e) => {
    e.stopPropagation();
    if (onViewDoctors) {
      onViewDoctors(hospital.id);
    } else {
      navigate(`/find-doctors?provider=${hospital.id}`);
    }
  };

  const rating = hospital.star_rating;

  const cardContent = (
    <Card
      sx={{
        borderRadius: 2.5,
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        transition: 'all 0.3s ease-in-out',
        cursor: 'pointer',
        overflow: 'visible',
        position: 'relative',
        background: `${theme.palette.secondary.main}30`,
        border: `1px solid ${theme.palette.background.paper}60`,
        '&:hover': {
          transform: 'translateY(-8px) scale(1.02)',
          boxShadow: `0 20px 40px ${theme.palette.primary.main}26`,
          background: `linear-gradient(135deg, ${theme.palette.secondary.main}40, ${theme.palette.secondary.light}20)`,
          zIndex: 10,
          '& .hospital-avatar': {
            transform: 'scale(1.1)',
          },
          '& .hospital-building-image': {
            transform: 'scale(1.05)',
          }
        },
      }}
      onClick={handleClick}
    >
      {/* Bookmark Button - Top Right Corner of Card */}
      <Button
        onClick={handleToggleFavorite}
        disabled={loading}
        startIcon={
          isFavorite ? (
            <Bookmark sx={{ fontSize: { xs: 18, md: 20 }, color: theme.palette.secondary.main }} />
          ) : (
            <BookmarkBorder sx={{ fontSize: { xs: 18, md: 20 }, color: '#666' }} />
          )
        }
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          minWidth: 'auto',
          height: { xs: 32, md: 36 },
          bgcolor: 'white',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          border: '2px solid',
          borderColor: isFavorite ? theme.palette.secondary.main : 'grey.300',
          borderRadius: 2,
          zIndex: 3,
          px: 1,
          py: 0.5,
          fontSize: '0.75rem',
          fontWeight: 600,
          color: isFavorite ? theme.palette.secondary.main : '#666',
          textTransform: 'none',
          whiteSpace: 'nowrap',
          '&:hover': {
            bgcolor: isFavorite ? '#ffe5e5' : 'grey.50',
            transform: 'scale(1.05)',
            boxShadow: '0 6px 16px rgba(0,0,0,0.3)',
          },
          transition: 'all 0.2s ease',
        }}
      >
        {isFavorite ? 'Saved' : 'Save'}
      </Button>

      <CardContent sx={{ p: { xs: 2, md: 2.5, lg: 2.5 }, height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Building Image */}
        {hospital.building_image && (
          <Box sx={{ mb: { xs: 1.5, md: 2 } }}>
            <Box
              className="hospital-building-image"
              component="img"
              src={hospital.building_image.startsWith('http') 
                ? hospital.building_image 
                : `${config.backend.host}${hospital.building_image}`}
              alt={`${hospital.name} building`}
              sx={{
                width: '100%',
                height: { xs: 120, md: 140 },
                objectFit: 'cover',
                borderRadius: 2,
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                transition: 'transform 0.3s ease',
              }}
            />
          </Box>
        )}

        {/* Header - Hospital Name & Type */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: { xs: 1.5, md: 2 } }}>
          <Avatar
            className="hospital-avatar"
            sx={{
              width: { xs: 45, md: 60 },
              height: { xs: 45, md: 60 },
              bgcolor: 'primary.main',
              border: '2px solid #fff',
              boxShadow: '0 3px 10px rgba(0,0,0,0.15)',
              transition: 'transform 0.3s ease',
            }}
          >
            <LocalHospital sx={{ fontSize: { xs: 20, md: 30 } }} />
          </Avatar>
          
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 900, 
                fontSize: { xs: '1.1rem', md: '1.3rem' },
                lineHeight: 1.2,
                mb: 0.3,
                color: '#1a1a1a',
              }}
            >
              {hospital.name}
            </Typography>
            <Typography 
              variant="body2" 
              color="primary" 
              sx={{ 
                fontWeight: 800, 
                fontSize: { xs: '0.9rem', md: '1rem' },
                lineHeight: 1.1,
              }}
            >
              {hospital.provider_type_name}
            </Typography>
          </Box>
        </Box>

        {/* Location & Distance */}
        <Box sx={{ mb: { xs: 1, md: 1.5 } }}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <LocationOn sx={{ fontSize: { xs: 14, md: 18 }, color: '#666' }} />
            <Typography 
              variant="body2" 
              sx={{ 
                fontSize: { xs: '0.9rem', md: '1rem' },
                fontWeight: 800,
                color: '#666',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {hospital.city && hospital.state 
                ? `${hospital.city}, ${hospital.state}` 
                : hospital.address_line1 || 'Location not available'}
            </Typography>
            {hospital.distance_km && (
              <Chip
                label={`${parseFloat(hospital.distance_km).toFixed(1)} km`}
                size="small"
                sx={{
                  height: { xs: 18, md: 20 },
                  fontSize: { xs: '0.6rem', md: '0.65rem' },
                  bgcolor: '#e3f2fd',
                  color: '#1976d2',
                  fontWeight: 700,
                  ml: 'auto !important',
                }}
              />
            )}
          </Stack>
          
          {/* Address Details */}
          {(hospital.address_line1 || hospital.locality) && (
            <Typography 
              variant="caption" 
              sx={{ 
                fontSize: { xs: '0.8rem', md: '0.85rem' },
                fontWeight: 800,
                color: '#888',
                display: 'block',
                mt: 0.2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {hospital.address_line1 || hospital.locality}
            </Typography>
          )}
        </Box>

        {/* Rating - Only show if star_rating is not null */}
        {rating !== null && (
          <Box sx={{ mb: { xs: 1, md: 1.5 } }}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Star sx={{ fontSize: { xs: 16, md: 18 }, color: '#ffa726' }} />
              <Typography variant="body2" sx={{ fontSize: { xs: '0.9rem', md: '1rem' }, fontWeight: 800 }}>
                {rating}
              </Typography>
            </Stack>
          </Box>
        )}

        {/* Specialties */}
        {hospital.specialties && hospital.specialties.length > 0 && (
          <Box sx={{ mb: { xs: 1.5, md: 2 } }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', md: '0.9rem' }, mb: 0.5, display: 'block', fontWeight: 800 }}>
              Specialties:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {hospital.specialties.slice(0, 3).map((specialty, idx) => {
                const specialtyId = specialty.id || specialty;
                const specialtyName = specialty.name || specialty;
                const isMatchingSpecialty = selectedSpecialty && specialtyId.toString() === selectedSpecialty.toString();

                return (
                  <Chip
                    key={idx}
                    label={specialtyName}
                    size="small"
                    sx={{
                      height: { xs: 18, md: 20 },
                      fontSize: { xs: '0.7rem', md: '0.75rem' },
                      fontWeight: 800,
                      ...(isMatchingSpecialty ? {
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
                        border: '2px solid',
                        borderColor: 'primary.light',
                        transform: 'scale(1.05)',
                      } : {
                        bgcolor: 'rgba(25, 118, 210, 0.1)',
                        color: 'primary.main',
                      }),
                      transition: 'all 0.2s ease-in-out',
                    }}
                  />
                );
              })}
              {hospital.specialties.length > 3 && (
                <Chip
                  label={`+${hospital.specialties.length - 3}`}
                  size="small"
                  sx={{
                    height: { xs: 18, md: 20 },
                    fontSize: { xs: '0.7rem', md: '0.75rem' },
                    bgcolor: 'rgba(0,0,0,0.1)',
                    color: 'text.secondary',
                    fontWeight: 800,
                  }}
                />
              )}
            </Box>
          </Box>
        )}

        {/* Footer - Single Action Button */}
        <Box sx={{ mt: 'auto' }}>
          <Button
            variant="contained"
            size="small"
            fullWidth
            sx={{
              borderRadius: 1.5,
              textTransform: 'none',
              fontWeight: 800,
              fontSize: { xs: '0.9rem', md: '0.95rem' },
              py: { xs: 0.8, md: 1 },
              boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
            }}
            onClick={handleViewDoctorsClick}
          >
            View Doctors
          </Button>
        </Box>
      </CardContent>
    </Card>
  );

  const component = (
    <>
      {showAnimation ? (
        <Grow
          in={true}
          timeout={500 + (index % 4) * 200}
          style={{ transformOrigin: '0 0 0' }}
        >
          {cardContent}
        </Grow>
      ) : (
        cardContent
      )}
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%', borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );

  return component;
};

export default HospitalCard;

